<template>
  <div class="card-design-catalog">
    <PageHeadlineWithLink
      class="card-design-catalog__head"
      title="利用できるカードデザイン一覧"
      link-title=""
    />
    <div class="feedback">
      <transition name="fade" @enter="enterErrorFeedback">
        <FeedbackError v-if="showableErrorFeedback" :message="errorMessage" />
      </transition>
    </div>
    <div
      v-resize="setStickyStyleIfNeeded"
      class="card-design-catalog__categories-wrapper"
      :style="stickyStyle"
    >
      <div class="card-design-catalog__categories">
        <ul class="item-boxes--scrollable">
          <li
            class="item-box"
            :class="{ selected_category: isSelectedCategory('0') }"
            @click="onClickCategory('0')"
          >
            <p class="subhead item-box__label">すべて</p>
          </li>
          <li
            v-for="category in cardDesignCategories"
            :key="category.id"
            class="item-box"
            :class="{
              selected_category: isSelectedCategory(category.id)
            }"
            @click="onClickCategory(category.id)"
          >
            <p class="subhead item-box__label">
              {{ category.name }}
            </p>
          </li>
        </ul>
      </div>
      <div class="card-design-catalog__categories--pulldown">
        <p class="body card-design-catalog__pulldown-label">
          カテゴリで絞り込む
        </p>
        <SortWithBlackChevronArea
          :options="cardDesignCategoriesForSort"
          :selected-value="selectedCategoryId"
          @change-sort="onClickCategory"
        />
      </div>
    </div>
    <div class="card-design-catalog__items">
      <div v-if="isLoading" class="card-design-catalog__loading">
        <Loading />
      </div>
      <div v-else class="card-design-catalog__item-boxes-wrapper">
        <div class="card-design-catalog__item-boxes">
          <div
            v-for="design in filteredCategories"
            :key="design.id"
            class="card-design-catalog__item-box"
          >
            <ImgLazy
              :image-url="design.image_url"
              :alt-text="design.name"
              aspect-ratio="1"
            />
            <p>カードデザインID: {{ design.id }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import SortWithBlackChevronArea from '../../atoms/sorts/SortWithBlackChevronArea.vue'
import Loading from '../../atoms/loadings/Loading.vue'
import PageHeadlineWithLink from '../../molecules/headlines/PageHeadlineWithLink.vue'
import FeedbackError from '../../atoms/feedbacks/feedback-error.vue'
import ImgLazy from '../../atoms/images/ImgLazy.vue'

export default {
  components: {
    SortWithBlackChevronArea,
    Loading,
    PageHeadlineWithLink,
    FeedbackError,
    ImgLazy
  },
  data() {
    return {
      cardDesigns: [],
      cardDesignCategories: [],
      cardDesignCategoriesForSort: [],
      isLoading: false,
      selectedCategoryId: '0',
      stickyStyle: {},
      showableErrorFeedback: false,
      errorMessage: ''
    }
  },
  computed: {
    filteredCategories() {
      if (this.selectedCategoryId === '0') {
        return this.cardDesigns
      }
      return this.cardDesigns.filter(design =>
        design.category_ids.includes(String(this.selectedCategoryId))
      )
    }
  },
  async mounted() {
    this.isLoading = true

    try {
      const designsResponse = await axios.get(
        '/api/v1/pickup/card_designs/bulk_order'
      )
      this.cardDesigns = designsResponse.data.data.map(card_designs => {
        return {
          id: card_designs.attributes.id,
          name: card_designs.attributes.name,
          image_url: card_designs.attributes.image_url,
          category_ids:
            card_designs.relationships.card_design_categories.data.map(
              v => v.id
            )
        }
      })

      const categoriesResponse = await axios.get(
        '/api/v1/pickup/card_design_categories/bulk_order'
      )
      this.cardDesignCategories = categoriesResponse.data.data.map(
        card_design_categories => {
          return {
            id: card_design_categories.attributes.id.toString(),
            name: card_design_categories.attributes.name
          }
        }
      )
      this.cardDesignCategoriesForSort = this.cardDesignCategories.map(v => ({
        key: v.name,
        value: v.id.toString()
      }))
      this.cardDesignCategoriesForSort = [{ key: 'すべて', value: '0' }].concat(
        this.cardDesignCategoriesForSort
      )

      this.isLoading = false
    } catch (error) {
      this.isLoading = false
      this.showableErrorFeedback = true
      this.errorMessage =
        error.response.data.message ||
        'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
    }
  },
  methods: {
    onClickCategory(clicked_id) {
      this.selectedCategoryId = clicked_id
    },
    isSelectedCategory(id) {
      return this.selectedCategoryId === id
    },
    setStickyStyleIfNeeded() {
      const headers = document.querySelectorAll('header.header')
      if (headers.length !== 1) {
        return {}
      }
      const headerBottomRect = headers[0].getBoundingClientRect().bottom
      this.stickyStyle = {
        position: 'sticky',
        top: `${headerBottomRect}px`
      }
    },
    enterErrorFeedback() {
      setTimeout(() => {
        this.showableErrorFeedback = false
      }, 3000)
    }
  }
}
</script>

<style lang="scss" scoped>
.card-design-catalog {
  &__head {
    margin-bottom: 2px;
  }

  &__categories {
    margin: auto;
    max-width: $max-contents-width;
    background: $white;

    @media all and (min-width: $base-contents-width) {
      display: none;
    }
  }

  &__categories--pulldown {
    max-width: $max-contents-width;
    margin: auto;
    padding: 0 16px;
    display: flex;
    align-items: center;

    @media screen and (max-width: $base-contents-width - 1) {
      display: none;
    }
  }

  &__categories-wrapper {
    max-width: 100%;
    background: $white;
    z-index: 1;
  }

  &__pulldown-label {
    color: $gray-1;
    padding-right: 24px;
  }

  &__loading {
    margin: 20px auto 0;
    height: 60px;
  }

  &__item-boxes-wrapper {
    margin: auto;
    padding: 24px 16px;
    max-width: $max-contents-width;

    @media screen and (max-width: $base-contents-width - 1) {
      padding: 16px 0;
    }
  }

  &__item-boxes {
    width: 100%;
    overflow-x: auto;
    white-space: nowrap;
    -webkit-overflow-scrolling: touch;
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    grid-gap: 16px;

    @media screen and (max-width: $base-contents-width - 1) {
      grid-template-columns: repeat(4, 1fr);
      grid-gap: 2px;
    }

    @media screen and (max-width: 474px) {
      grid-template-columns: repeat(3, 1fr);
      grid-gap: 2px;
    }

    @media screen and (max-width: 319px) {
      grid-template-columns: repeat(2, 1fr);
      grid-gap: 2px;
    }
  }

  &__item-box {
    max-width: none;
    height: 100%;

    img {
      width: 100%;
    }
  }

  .item-box {
    transition: 0.3s;
    cursor: pointer;
    font-weight: bold;
    border-bottom: medium solid $gray-2;
    margin-right: 4px;
    width: auto;
    padding: 0;

    &.selected_category {
      transition: 0.3s;
      border-bottom: medium solid $primary;
    }

    &__label {
      margin: 16px 24px;
    }
  }
}

.item-boxes--scrollable {
  margin: 0;
  padding: 0;

  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
